import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { isMobile } from "../utils";
import { useAuth } from "./AuthContext";
import { useBroker } from "./BrokerContext";
import { PoweredByVelotax } from "../components/PoweredByVelotax";

interface ISideBarContext {
  modules: any[];
  reviewAbled: boolean;
  updateModules: (moduleParam: { name: string; completed: boolean }) => void;
  updateAllModules: (modules: IModule[]) => void;
  dark: boolean;
  handleTheme: Function;
  showDrawer: boolean;
  setShowDrawer: Function;
  chatNotificationQtt: number;
  setChatNotificationQtt: Dispatch<SetStateAction<number>>;
}

export interface IModule {
  name: string;
  completed: boolean;
}

export const SideBarContext = createContext<ISideBarContext>(
  {} as ISideBarContext
);

export const SidebarProvider: React.FC = ({ children }) => {
  const { user } = useAuth();
  const { currentBroker } = useBroker();
  const [dark, setDark] = useState(true);
  const [modules, setModules] = useState<any[]>([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [chatNotificationQtt, setChatNotificationQtt] = useState(0);

  const setTheme = useCallback((dark: boolean) => {
    if (dark) {
      document.body.classList.add("dark");
      setDark(true);
    } else {
      document.body.classList.remove("dark");
      setDark(false);
    }
  }, []);

  const handleTheme = () => {
    const dark = document.body.classList.contains("dark");
    setTheme(!dark);
  };

  useEffect(() => {
    setTheme(true);
  }, [setTheme]);

  const updateModules = useCallback(
    (moduleParam: IModule) => {
      const moduleIndex = modules.findIndex(
        (module) => module.name === moduleParam.name
      );

      const newModules = [...modules];
      if (moduleIndex !== -1) {
        newModules[moduleIndex] = moduleParam;
      } else {
        newModules.push(moduleParam);
      }

      setModules(newModules);
    },
    [modules]
  );

  const updateAllModules = useCallback((newModules: IModule[]) => {
    setModules(newModules);
  }, []);

  const modulesStatus = useMemo(() => user?.user?.modulesStatus || [], [user]);
  useEffect(() => {
    setModules(modulesStatus);
  }, [modulesStatus]);

  const reviewAbled = useMemo(
    () =>
      modules
        .map((module) => module.completed)
        .reduce((pre, cur) => pre && cur, true),
    [modules]
  );

  return (
    <SideBarContext.Provider
      value={{
        modules,
        reviewAbled,
        updateModules,
        updateAllModules,
        dark,
        handleTheme,
        showDrawer,
        setShowDrawer,
        chatNotificationQtt,
        setChatNotificationQtt,
      }}
    >
      {children}
      {isMobile() && !currentBroker.hidePoweredBy && false && (
        <PoweredByVelotax className="footer-secure footer-velotax" />
      )}
    </SideBarContext.Provider>
  );
};

export const useSideBar = (): ISideBarContext => useContext(SideBarContext);